import { ApiResponse } from "helpers/fetch-wrapper";
import { AdsRequests } from "queries/ads-queries";

export const PLACEMENTS = [
    { value: "FB_FEED", label: "Facebook Feed" },
    { value: "IG_FEED", label: "Instagram Feed" },
    { value: "FB_REELS_STORIES", label: "Facebook Reels & Stories" },
    { value: "IG_REELS_STORIES", label: "Instagram Reels & Stories" },
    { value: "MESSENGER", label: "Messenger" },
    { value: "AUDIENCE_NETWORK", label: "Audience Network" },
] as const;

export type Placement = (typeof PLACEMENTS)[number]["value"];

export const placementLabel = (value: string) => PLACEMENTS.find((p) => p.value === value)?.label ?? value;

export type CampaignStatus = "AWAITING_PAYMENT" | "IN_REVIEW" | "ACTIVE" | "PAUSED" | "COMPLETED" | "REJECTED";

/** Rates worked out by the API from the totals; null when there's nothing to divide by yet. */
export interface AdMetrics {
    ctr: number | null;
    cpc: number | null;
    cpm: number | null;
    cost_per_purchase: number | null;
    roas: number | null;
}

export interface AdResults {
    spend: string | number;
    reach: number;
    impressions: number;
    clicks: number;
    store_visits: number;
    add_to_cart: number;
    checkouts: number;
    purchases: number;
    revenue: string | number;
}

export interface PlacementRow { placement: string; impressions: number; clicks: number; purchases: number; spend: number }
export interface AgeGenderRow { age: string; male: number; female: number }
export interface LocationRow { location: string; people: number }
export interface DailyRow { date: string; spend: number; impressions: number; clicks: number; purchases: number }

export interface PendingRequest {
    action: "pause" | "resume" | "budget";
    budget?: string;
    note?: string;
    requested_at: string;
}

export interface Campaign extends AdResults {
    id: string;
    title: string;
    promote: "STOREFRONT" | "PRODUCT";
    product_id: string | null;
    product_image: string | null;
    creative_type: "UPLOAD" | "CREATE_FOR_ME";
    creative_urls: string[];
    placements: string[];
    budget_ngn: string;
    duration_days: number;
    payment_status: "PENDING" | "PAID" | "FAILED";
    status: CampaignStatus;
    status_label: string;
    live_ad_url: string;
    starts_at: string | null;
    ends_at: string | null;
    last_synced_at: string | null;
    pending_request: PendingRequest | null;
    created_at: string;
    metrics: AdMetrics;
}

export interface CampaignDetail extends Campaign {
    creative_notes: string;
    meta_ad_id: string;
    by_placement: PlacementRow[];
    by_age_gender: AgeGenderRow[];
    by_location: LocationRow[];
    daily: DailyRow[];
}

export interface AdsSummary {
    days: number;
    campaign_count: number;
    active_count: number;
    has_paid_campaign: boolean;
    last_synced_at: string | null;
    totals: AdResults & { metrics: AdMetrics };
    by_placement: PlacementRow[];
    by_age_gender: AgeGenderRow[];
    by_location: LocationRow[];
    daily: DailyRow[];
}

export interface NewCampaign {
    promote: "STOREFRONT" | "PRODUCT";
    productId?: string;
    creativeType: "UPLOAD" | "CREATE_FOR_ME";
    creativeUrls: string[];
    creativeNotes: string;
    placements: string[];
    budget: number;
    durationDays: number;
}

async function call<T>(request: () => Promise<Response>, fallback: string, okStatus = [200, 201]): Promise<ApiResponse<T>> {
    try {
        const response = await request();
        const data = await response.json().catch(() => ({}));
        const ok = okStatus.includes(response.status);
        return {
            body: data as T,
            message: data?.message,
            status: response.status,
            error: ok ? null : data?.message || fallback,
        };
    } catch {
        return { body: null as T, message: fallback, status: 500, error: fallback };
    }
}

export const getCampaignsAction = () =>
    call<Campaign[]>(() => AdsRequests.listCampaigns(), "Couldn't load your campaigns.");

export const getCampaignAction = (campaignId: string) =>
    call<CampaignDetail>(() => AdsRequests.getCampaign(campaignId), "Couldn't load this campaign.");

export const getAdsSummaryAction = (days: number) =>
    call<AdsSummary>(() => AdsRequests.getSummary(days), "Couldn't load your ad results.");

export const createCampaignAction = (campaign: NewCampaign) =>
    call<{ id: string; reference: string; authorization_url: string }>(
        () => AdsRequests.createCampaign(campaign), "Couldn't start your boost. Please try again.");

export const requestCampaignChangeAction = (campaignId: string, change: { action: string; budget?: number; note?: string }) =>
    call<CampaignDetail>(() => AdsRequests.requestChange(campaignId, change), "Couldn't send your request.");

export const uploadCreativeAction = (file: File) =>
    call<{ url: string; kind: "image" | "video" }>(() => AdsRequests.uploadCreative(file), "The upload didn't go through.");

/** Dashboard timeframe values (see DashboardDropdown) as a number of days; 0 means all time. */
export const timeframeDays = (timeframe: string) =>
    ({ "7d": 7, "28d": 28, "3m": 90, "1y": 365 } as Record<string, number>)[timeframe] ?? 0;
